import * as React from 'react';

import * as _ from 'lodash';

import { connect } from 'react-redux';

import {
  withStyles,
  WithStyles,
  createStyles,
  Theme
} from '@material-ui/core/styles';

import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

import Fade from '@material-ui/core/Fade';
import Zoom from '@material-ui/core/Zoom';

import { AutoSizer, Size } from 'react-virtualized';

import { textTruncate } from '@catalogit/common/lib/constants/typography';
import { FULFILLED } from '@catalogit/common/lib/types/states';
import {
  getPublicHighResImage,
  getPlaceholderMedium,
  getPublicThumbnail,
  getDimensions,
  getScaledDimensions
} from '@catalogit/common/lib/utils/media-utils';

import { hasNamePath } from '../constants/predicates';

import {
  IStoreState,
  IEntryState,
  IMediumState,
  HUBThunkDispatch,
  IMediumRecord
} from '../types/store';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      position: 'fixed',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: theme.zIndex.tooltip + 500,

      display: 'flex',
      flexDirection: 'column',

      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(2)
      }
    },

    title: {
      flexGrow: 1,

      ...textTruncate,
      whiteSpace: 'nowrap',
      color: 'inherit'
    },

    thumbnailViewer: {
      flexShrink: 0,
      flexGrow: 0,
      display: 'flex',
      overflowX: 'scroll',
      WebkitOverflowScrolling: 'touch',
      alignSelf: 'center',
      maxWidth: '100%'
    },

    thumbnailViewerInner: {
      display: 'flex',
      padding: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px ${theme.spacing(
        1
      )}px`
    },

    thumbnailWrapper: {
      flexShrink: 0,
      flexGrow: 0,
      width: 75,
      height: 75,
      overflow: 'hidden',
      marginRight: theme.spacing(1)
    },

    thumbnail: {
      width: 75,
      height: 75,
      transition: '0.3s',

      '&:hover': {
        transform: 'scale(1.1)'
      }
    },

    selected: {
      borderColor: '#FF8F00',
      boxShadow: '0 0 10px #FF8F00'
    },

    mediumViewer: {
      flexShrink: 1,
      flexGrow: 1,
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',

      '& img': {
        margin: `0 auto`
      }
    },

    imgWrapper: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',

      '& img': {
        position: 'absolute'
      }
    }
  });

type JSSProps = WithStyles<typeof styles, true>;

interface IReduxProps {
  entryState: IEntryState;
  mediaState: IMediumState;

  dispatch: HUBThunkDispatch;
}

interface IExternalProps {
  euid: string;
  mediumIdx: number;

  onClose: () => void;
}

type IProps = JSSProps & IReduxProps & IExternalProps;

interface IState {
  open: boolean;
  loading: boolean;
  img?: HTMLImageElement;

  imgAVisible: boolean;
  imgAIn: boolean;
  imgAIdx: number;
  imgBIn: boolean;
  imgBIdx: number;
}

class MediaViewer extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      open: true,
      loading: false,
      img: undefined,

      imgAVisible: true,
      imgAIn: true,
      imgAIdx: props.mediumIdx,
      imgBIn: false,
      imgBIdx: props.mediumIdx
    };
  }

  handleClose = () => {
    this.setState({ open: false });
  };

  handleThumbnailClick = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { idx } = e.currentTarget.dataset;
    const iIdx = parseInt(idx || '0', 10);

    // no-op if clicking on the same image
    if (
      iIdx ===
      (this.state.imgAVisible ? this.state.imgAIdx : this.state.imgBIdx)
    ) {
      return;
    }

    const { euid, entryState, mediaState } = this.props;

    const entry = entryState.get(euid);
    if (!entry || (entry._meta && entry._meta.state !== FULFILLED)) {
      return;
    }

    const mediaIds = entry.media;
    const medium = mediaIds ? mediaState.get(mediaIds[iIdx]) : undefined;
    const { path } =
      (medium && getPublicHighResImage(medium)) ||
      getPublicHighResImage(getPlaceholderMedium());

    // if a previously selected image is still loading cancel it
    if (this.state.loading && this.state.img) {
      const { img } = this.state;
      img.src = '';
    }

    const img = new Image();
    img.onload = () => {
      this.setState((currState) => {
        if (currState.imgAVisible) {
          return {
            loading: false,
            imgAIn: true,
            imgBIn: false
          };
        } else {
          return {
            loading: false,
            imgAIn: false,
            imgBIn: true
          };
        }
      });
    };
    img.src = path;

    if (this.state.imgAVisible) {
      this.setState({
        imgAVisible: false,
        imgAIn: false,
        imgBIdx: iIdx,
        loading: true
      });
    } else {
      this.setState({
        imgAVisible: true,
        imgBIn: false,
        imgAIdx: iIdx,
        loading: true
      });
    }
  };

  render() {
    const { euid, entryState, mediaState, classes, theme } = this.props;
    const { imgAIdx, imgBIdx } = this.state;
    const imageIdx = this.state.imgAVisible ? imgAIdx : imgBIdx;

    const entry = entryState.get(euid);
    if (!entry || (entry._meta && entry._meta.state !== FULFILLED)) {
      return null;
    }

    const name =
      (entry.properties && _.get(entry.properties, hasNamePath)) || '';

    const mediaIds = entry.media;

    // if no mediaIds then there's nothing to display
    if (!mediaIds) {
      return null;
    }

    return (
      <Zoom in={this.state.open} appear={true} onExited={this.props.onClose}>
        <div className={classes.container}>
          <AppBar position='static' color='default'>
            <Toolbar>
              <Typography variant='h6' className={classes.title}>
                {name}
              </Typography>
              <Button
                onClick={this.handleClose}
                style={{ color: 'inherit', marginRight: -theme.spacing(2) }}
              >
                Done
              </Button>
            </Toolbar>
          </AppBar>

          {mediaIds.length > 1 && (
            <div className={classes.thumbnailViewer}>
              <div className={classes.thumbnailViewerInner}>
                {mediaIds.map((muid: string, idx: number) => {
                  const medium = mediaState.get(muid);
                  const { path } = medium
                    ? getPublicThumbnail(medium)
                    : getPublicThumbnail(getPlaceholderMedium());
                  return (
                    <div
                      key={muid}
                      className={`${classes.thumbnailWrapper}${
                        idx === imageIdx ? ` ${classes.selected}` : ''
                      }`}
                    >
                      <img
                        src={path}
                        className={classes.thumbnail}
                        data-idx={idx}
                        onClick={this.handleThumbnailClick}
                        alt={
                          medium?.name ||
                          medium?.description ||
                          `${name} media ${idx + 1}`
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div
            className={classes.mediumViewer}
            style={{ marginTop: mediaIds.length > 1 ? 0 : theme.spacing(2) }}
          >
            {/* ???: without width: 100% chrome is flashing when hovering over thumbnails */}
            <AutoSizer style={{ width: '100%' }}>
              {(size: Size) => {
                if (!mediaIds) {
                  return (
                    <div
                      className={classes.imgWrapper}
                      style={{ width: size.width, height: size.height }}
                    >
                      No media exist for this Entry
                    </div>
                  );
                }

                let imgB;
                if (imgAIdx !== imgBIdx) {
                  const mediumB = mediaState.get(
                    mediaIds[imgBIdx]
                  ) as IMediumRecord;

                  const originalPathB = mediumB.getIn([
                    'derivatives',
                    'public_original',
                    'path'
                  ]) as string;

                  const useOriginalB =
                    originalPathB &&
                    mediumB.get('content_type') === 'application/pdf';

                  const {
                    path: pathB,
                    width: widthB,
                    height: heightB
                  } = (mediumB &&
                    (useOriginalB
                      ? {
                          path: originalPathB,
                          width: size.width,
                          height: size.height
                        }
                      : getPublicHighResImage(mediumB))) ||
                  getPublicHighResImage(getPlaceholderMedium());

                  const { width: sWidthB, height: sHeightB } =
                    getScaledDimensions(getDimensions(widthB, heightB), size);

                  const titleAltB =
                    mediumB?.name ||
                    mediumB?.description ||
                    `${name} media ${imgBIdx + 1}`;

                  imgB = (
                    <Fade in={this.state.imgBIn}>
                      {useOriginalB ? (
                        <iframe
                          title={titleAltB}
                          src={`${pathB}#toolbar=1`}
                          style={{
                            width: sWidthB,
                            height: sHeightB,
                            zIndex: this.state.imgAVisible ? 0 : -1,
                            display: this.state.imgBIn ? '' : 'none'
                          }}
                        ></iframe>
                      ) : (
                        <img
                          src={pathB}
                          alt={titleAltB}
                          style={{
                            width: sWidthB,
                            height: sHeightB,
                            zIndex: this.state.imgAVisible ? -1 : 0
                          }}
                        />
                      )}
                    </Fade>
                  );
                }

                const mediumA = mediaState.get(
                  mediaIds[imgAIdx]
                ) as IMediumRecord;

                const originalPathA = mediumA.getIn([
                  'derivatives',
                  'public_original',
                  'path'
                ]) as string;

                const useOriginalA =
                  originalPathA &&
                  mediumA.get('content_type') === 'application/pdf';

                const {
                  path: pathA,
                  width: widthA,
                  height: heightA
                } = (mediumA &&
                  (useOriginalA
                    ? {
                        path: originalPathA,
                        width: size.width,
                        height: size.height
                      }
                    : getPublicHighResImage(mediumA))) ||
                getPublicHighResImage(getPlaceholderMedium());

                const { width: sWidthA, height: sHeightA } =
                  getScaledDimensions(getDimensions(widthA, heightA), size);

                const titleAltA =
                  mediumA.name ||
                  mediumA.description ||
                  `${name} media ${imgAIdx + 1}`;

                return (
                  <div
                    className={classes.imgWrapper}
                    style={{ width: size.width, height: size.height }}
                  >
                    <Fade in={this.state.imgAIn}>
                      {useOriginalA ? (
                        <iframe
                          title={titleAltA}
                          src={`${pathA}#toolbar=1`}
                          style={{
                            width: sWidthA,
                            height: sHeightA,
                            zIndex: this.state.imgAVisible ? 0 : -1,
                            display: this.state.imgAIn ? '' : 'none'
                          }}
                        ></iframe>
                      ) : (
                        <img
                          src={pathA}
                          alt={titleAltA}
                          style={{
                            width: sWidthA,
                            height: sHeightA,
                            zIndex: this.state.imgAVisible ? 0 : -1
                          }}
                        />
                      )}
                    </Fade>
                    {imgB}
                  </div>
                );
              }}
            </AutoSizer>
          </div>
        </div>
      </Zoom>
    );
  }
}

export default withStyles(styles, { withTheme: true })(
  connect((state: IStoreState) => ({
    entryState: state.entry,
    mediaState: state.media
  }))(MediaViewer)
);
